import { reactEvents } from '../../../bridge/reactEvents';
import { navigationStore } from '../../../bridge/navigationStore';
import { productApprovalEvents } from '../../../react/components/shared/productApproval/events';

// @ngInject
const scrollTop = ($window) => $window.scrollTo(0, 0);

// @ngInject
const articleResolve = ($stateParams, ArticlesApiService, $q) =>
  ArticlesApiService.getById($stateParams.articleId, {
    includeRelations: ['owner', 'matrixNodeX', 'matrixNodeY', 'productGroup'],
  }).catch((error) =>
    $q.reject({ ...error, articleNotFound: error.status === 404 }),
  );

// @ngInject
const articleIdByItemCodeResolve = ($stateParams, ArticlesApiService, $q) =>
  ArticlesApiService.getIdByItemCode($stateParams.articleNumber).catch(
    (error) => $q.reject({ ...error, articleNotFound: error.status === 404 }),
  );

// @ngInject
const redirectToArticle = (articleId) => ({
  state: 'article',
  params: { articleId },
});

// @ngInject
const rootFolderIdResolve = (article) => article.folderId;

// @ngInject
const variantsResolve = (article, ArticlesApiService) =>
  ArticlesApiService.getVariantsByArticleId(article.id);

// @ngInject
const productGroupsResolve = (currentOrganization, ProductGroupsApiService) =>
  ProductGroupsApiService.getAllByOrganizationId(currentOrganization.id);

// @ngInject
const config = ($stateProvider) => {
  $stateProvider.state('article-by-number', {
    parent: 'main',
    url: '/articles/by-number?articleNumber',
    data: { pageTitle: 'Article' },
    resolve: {
      articleId: articleIdByItemCodeResolve,
    },
    redirectTo: redirectToArticle,
  });

  $stateProvider.state('articleNotFound', {
    parent: 'main',
    url: '/article-not-found?articleId&articleNumber',
    views: {
      'main-content': {
        template: '<article-not-found-page></article-not-found-page>',
      },
    },
    data: { pageTitle: 'Article not found' },
  });

  $stateProvider.state('article', {
    parent: 'main',
    url: '/articles/{articleId}',
    views: {
      'main-content': {
        controller: 'ArticleController as vm',
        templateUrl: 'main/article/article.tpl.html',
      },
    },
    data: { pageTitle: 'Article' },
    resolve: {
      article: articleResolve,
      variants: variantsResolve,
      productGroups: productGroupsResolve,
      variantOptions() {
        return { selectedBranch: null };
      },
    },
    onEnter: scrollTop,
  });

  // Files tab
  $stateProvider.state('article.files', {
    url: '/files',
    abstract: true,
    templateUrl: 'tabs/filesTab/filesTab.tpl.html',
    controller: 'FilesTabsController as vm',
    resolve: {
      rootFolderId: rootFolderIdResolve,
      isArticle: () => true,
      concept: () => null,
      customer: () => null,
      supplier: () => null,
    },
  });

  $stateProvider.state('article.files.id', {
    url: '/{folderPath:nonURIEncoded}?{identifier:string}',
    params: {
      folderPath: { value: null, squash: true },
      identifier: { value: null, squash: true },
    },
  });

  // Tasks tab
  $stateProvider.state('article.tasks', {
    url: '/tasks',
    templateUrl: 'tabs/tasksTab/tasksTab.tpl.html',
    controller: 'TasksTabsController as vm',
    resolve: {
      concept: () => null,
      customer: () => null,
      tasksTabSettings: () => ({ hideBadges: false }),
    },
    params: {
      toDoTaskId: { value: null },
    },
  });
};

class ArticleController {
  constructor(
    $rootScope,
    $state,
    $scope,
    DS,
    article,
    variants,
    ArticleVariantDialogService,
    ArticlesApiService,
    SavingService,
    AuthService,
  ) {
    'ngInject';

    this.betaFeatures = false;

    $rootScope.pageTitle = `${article.itemCode || ''} - Article`;
    reactEvents.entityVisited.dispatch({
      id: article.id,
      type: 'article',
      label: article.itemCode,
    });

    this.DS = DS;
    this.article = article;
    this.variants = variants;
    this.nodeGroup = {
      nodeXValues: this.article.nodeXValues,
      nodeYValues: this.article.nodeYValues,
    };

    this.displayVariants = this.getDisplayVariants(variants);
    this.ArticleVariantDialogService = ArticleVariantDialogService;
    this.ArticlesApiService = ArticlesApiService;
    this.SavingService = SavingService;
    this.$state = $state;
    this.$scope = $scope;

    if (this.$state.params.variantId) {
      this.setCurrentVariant(this.$state.params.variantId);
    } else {
      const currentVariant =
        _.find(variants, { isProductDevelopment: true }) ||
        _.find(variants, { id: article.mainVariantId }) ||
        variants[0];

      if (currentVariant == undefined) {
        this.setCurrentVariant(0);
      } else {
        this.setCurrentVariant(currentVariant.id);
      }
    }

    this.getDisplayText = (article) => article.itemCode;
    this.goToManagement = () => $state.go('article.management');

    this.entityTabs = [
      {
        heading: '<i class="icon icon-settings"></i>',
        tooltip: 'Management',
        route: 'article.management',
        isDefault: true,
      },
      {
        heading: '<i class="icon icon-folder"></i>',
        tooltip: 'Files',
        route: 'article.files.id',
      },
      {
        heading: '<i class="icon icon-assignment"></i>',
        tooltip: 'Tasks',
        route: 'article.tasks',
      },
      {
        heading: '<i class="icon icon-eye"></i>',
        tooltip: 'Product approval',
        route: 'article.productApproval',
      },
    ];

    this.contentTabs = [
      {
        heading: 'Overview',
        route: 'variant.overview',
        active: this.$state.is('article'),
      },
      {
        heading: 'Options',
        route: 'variant.options',
        hidden: !this.currentVariant.isProductDevelopment,
      },
      {
        heading: 'Suppliers',
        route: 'variant.suppliers',
      },
      {
        heading: 'Versions',
        route: 'variant.versions',
        hidden: !this.currentVariant.isProductDevelopment,
      },
      {
        heading: 'Inquiry',
        route: 'variant.inquiry',
        hidden:
          !this.betaFeatures ||
          this.currentVariant.isProductDevelopment !== true,
      },
      {
        heading: 'Offers',
        route: 'variant.offers',
        hidden:
          !this.betaFeatures ||
          this.currentVariant.isProductDevelopment !== true,
      },
      {
        heading: 'Purchase',
        route: 'variant.purchase',
        hidden: this.currentVariant.isProductDevelopment,
      },
      {
        heading: 'Sales',
        route: 'variant.sales',
        hidden: this.currentVariant.isProductDevelopment,
      },
      {
        heading: 'Matrix',
        route: 'variant.matrix',
        hidden:
          !article.matrixNodeY ||
          !article.matrixNodeX ||
          this.currentVariant.isProductDevelopment,
      },
      {
        heading: 'Garp',
        route: 'variant.general.garp',
        hidden: this.currentVariant.isProductDevelopment,
      },
    ];

    _.map(this.contentTabs, (tab) => {
      tab.params = () => this.getCurrentVariantId();
    });

    AuthService.getUser().then((user) => {
      this.betaFeatures = user.betaFeatures;
      this._updateBetaTabVisibility();
      this.publishNavigationContext();
    });

    this.currentUserUpdatedEvent = reactEvents.currentUserUpdated.subscribe(
      () => {
        AuthService.getUser().then((user) => {
          this.betaFeatures = user.betaFeatures;
          this._updateBetaTabVisibility();
          this.publishNavigationContext();
        });
      },
    );

    $scope.$on('$stateChangeSuccess', () => {
      if (this.$state.is('article') || this.$state.is('variant')) {
        this.goToDefaultState();
      }
    });

    this.variantNameChangedEvent = reactEvents.variantNameChanged.subscribe(
      (variant) => {
        this.setVariantName(variant);
      },
    );
    this.newArticleVariantRequestedEvent =
      reactEvents.newArticleVariantRequested.subscribe(() => {
        this.newVariant();
      });
    this.variantAddedEvent = reactEvents.newOptionAddedWithVariant.subscribe(
      () => {
        ArticlesApiService.getById(article.id, {
          includeRelations: [
            'owner',
            'matrixNodeX',
            'matrixNodeY',
            'productGroup',
          ],
        }).then((article) => {
          this.article = article;
        });
        ArticlesApiService.getVariantsByArticleId(article.id).then(
          (updatedVariants) => {
            const newVariant = _.maxBy(updatedVariants, 'id');
            this.addVariant(newVariant);
          },
        );
      },
    );

    const reloadVariants = () => {
      ArticlesApiService.getVariantsByArticleId(article.id).then(
        (updatedVariants) => {
          this.variants = updatedVariants;
          this.displayVariants = this.getDisplayVariants(updatedVariants);
          this.publishNavigationContext();
          this.$scope.$applyAsync();
        },
      );
    };

    this.variantArchivedEvent =
      reactEvents.variantArchived.subscribe(reloadVariants);
    this.variantRestoredEvent =
      reactEvents.variantRestored.subscribe(reloadVariants);
    this.variantOptionLinkedEvent =
      reactEvents.variantOptionLinked.subscribe(reloadVariants);

    const reloadArticle = () => {
      ArticlesApiService.getById(article.id, {
        includeRelations: [
          'owner',
          'matrixNodeX',
          'matrixNodeY',
          'productGroup',
        ],
      }).then((updatedArticle) => {
        this.article = updatedArticle;
        this.publishNavigationContext();
        this.$scope.$applyAsync();
      });
    };

    this.articleVersionUpdatedEvent =
      reactEvents.articleVersionUpdated.subscribe(reloadArticle);
    this.articleUpdatedEvent = reactEvents.articleUpdated.subscribe(
      (articleId) => {
        if (articleId === article.id) reloadArticle();
      },
    );
    this.articleFinalizedEvent =
      reactEvents.variantFinalized.subscribe(reloadArticle);

    this.productApprovalCreated =
      productApprovalEvents.productApprovalsCreated.subscribe(() => {
        this.article.hasAnyPa = true;
      });

    this.setupWatchers();
    this.publishNavigationContext();
  }

  goToDefaultState() {
    const pdVariant = _.find(this.variants, { isProductDevelopment: true });
    const initialTab = pdVariant
      ? {
          route: 'variant.overview',
          params: () => ({ variantId: pdVariant.id }),
        }
      : _.find(this.entityTabs, { isDefault: true });
    if (initialTab) {
      this.$scope.$applyAsync(() => {
        this.$state.go(initialTab.route, initialTab.params?.(), {
          location: 'replace',
        });
      });
    }
  }

  getCurrentVariantId() {
    if (this.currentVariant == undefined) {
      return { variantId: 0 };
    }
    return { variantId: this.currentVariant.id };
  }

  newVariant() {
    this.ArticleVariantDialogService.showDialog({
      articleId: this.article.id,
    }).then((newVariant) => {
      this.addVariant(newVariant);
      this.changeVariant(newVariant.id);
    });
  }

  editVariant(variant, event) {
    event.stopPropagation();
    event.preventDefault();

    this.ArticleVariantDialogService.showDialog({ variant });
  }

  changeVariant(variantId) {
    this.$state.go('variant.overview', { variantId });
  }

  setCurrentVariant(variantId) {
    variantId = parseInt(variantId, 10);
    this.currentVariant = _.find(this.variants, { id: variantId });
  }

  setVariantName(variant) {
    if (this.currentVariant.id === variant.id) {
      this.currentVariant.name = variant.value;
    }

    const foundVariant = this.variants.find((v) => v.id === variant.id);
    if (foundVariant) {
      foundVariant.name = variant.value;
    }
    this.displayVariants = this.getDisplayVariants(this.variants);
    this.publishNavigationContext();
    this.$scope.$applyAsync();
  }

  addVariant(variant) {
    this.variants.push(variant);
    this.displayVariants = this.getDisplayVariants(this.variants);
    this.publishNavigationContext();
    return variant;
  }

  getActiveTab() {
    return _.find(this.contentTabs, { active: true });
  }

  _updateBetaTabVisibility() {
    ['Inquiry', 'Offers'].forEach((heading) => {
      const tab = _.find(this.contentTabs, { heading });
      if (tab) {
        tab.hidden =
          !this.betaFeatures ||
          this.currentVariant.isProductDevelopment !== true;
      }
    });
  }

  archivedExists() {
    return !!_.find(this.variants, { archived: true });
  }

  getDisplayVariants(variants) {
    return _.map(variants, (variant) => ({
      id: variant.id,
      name: variant.name,
      isMain: this.isMainVariant(variant),
      isProductDevelopment: variant.isProductDevelopment,
      optionLinkId: variant.optionLinkId,
      archived: variant.archived,
    }));
  }

  isMainVariant(variant) {
    return variant.id === this.article.mainVariantId ? true : false;
  }

  publishNavigationContext() {
    navigationStore.setContext({
      type: 'article',
      betaFeatures: this.betaFeatures,
      customer: {
        id: this.article.customerId,
        name:
          this.article.customerName ||
          this.article.customer?.name ||
          'Customer',
      },
      concept: this.article.conceptId
        ? {
            id: this.article.conceptId,
            name:
              this.article.conceptName ||
              this.article.concept?.name ||
              'Concept',
          }
        : undefined,
      article: {
        id: this.article.id,
        name: this.article.name || this.article.itemCode,
        itemCode: this.article.itemCode,
        archived: this.article.archived,
        basedOnId: this.article.basedOnId,
        basedOnArticleNumber: this.article.basedOnArticleNumber,
        hasMatrix: !!this.article.matrixNodeY && !!this.article.matrixNodeX,
        isVdc: !!this.article.isVariable,
        collectionItem: !!this.article.collectionItem,
        syncedVariantId: this.article.syncedVariantId,
      },
      currentVariantId: this.currentVariant?.id ?? null,
      variants: this.getDisplayVariants(this.variants),
    });
  }

  setupWatchers() {
    this.$scope.$watch(
      () => this.$state.params.variantId,
      (newValue, oldValue) => {
        if (newValue && newValue !== oldValue) {
          this.setCurrentVariant(newValue);

          _.map(this.contentTabs, (tab) => {
            tab.params = () => this.getCurrentVariantId();
            if (
              tab.heading === 'Sales' ||
              tab.heading === 'Purchase' ||
              tab.heading === 'Garp'
            ) {
              tab.hidden = this.currentVariant.isProductDevelopment;
            } else if (tab.heading === 'Matrix') {
              tab.hidden =
                !this.article.matrixNodeY ||
                !this.article.matrixNodeX ||
                this.currentVariant.isProductDevelopment;
            } else if (
              tab.heading === 'Compositions' ||
              tab.heading === 'Options' ||
              tab.heading === 'Versions'
            ) {
              tab.hidden = this.currentVariant.isProductDevelopment !== true;
            } else if (tab.heading === 'Inquiry' || tab.heading === 'Offers') {
              tab.hidden =
                !this.betaFeatures ||
                this.currentVariant.isProductDevelopment !== true;
            }
          });
          this.publishNavigationContext();
        }
      },
    );

    this.$scope.$watch(
      () => this.article.mainVariantId,
      (newValue, oldValue) => {
        if (newValue !== oldValue) {
          this.displayVariants = this.getDisplayVariants(this.variants);
          this.publishNavigationContext();
        }
      },
    );

    const unsub = reactEvents.onMatrixColorAxisUpdate.sub((value) => {
      this.article.colorAxis = value;
    });

    this.$scope.$on('$destroy', () => {
      this.variantNameChangedEvent();
      this.productApprovalCreated();
      this.variantAddedEvent();
      this.variantArchivedEvent();
      this.variantRestoredEvent();
      this.variantOptionLinkedEvent();
      this.articleVersionUpdatedEvent();
      this.articleUpdatedEvent();
      this.articleFinalizedEvent();
      this.currentUserUpdatedEvent();
      this.newArticleVariantRequestedEvent();
      unsub();
    });
  }
}

angular
  .module('main.article', [
    'main.article.variant',
    'main.article.management',
    'main.article.productApproval',
  ])
  .config(config)
  .controller('ArticleController', ArticleController);
